import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAtom } from "jotai";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  FaEye,
  FaEdit,
  FaTrash,
  FaPlus,
  FaSearch,
  FaFilePdf,
} from "react-icons/fa";
import axios from "axios";
import toast from "react-hot-toast";
import { confirmAlert } from "react-confirm-alert";

import Modal from "../../../components/Modal/Modal";
import ErrorBoundaryRoutes from "../../../components/shared/ErrorBoundaryRoutes";
import { gbState } from "../../../components/shared/GlobalState";
import NewsView from "./NewsView";
import NewsAdd from "../Create/NewsAdd";

const NewsListChild = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [addShowModal, setAddShowModal] = useState(false);
  const [currentRecord, setCurrentRecord] = useState({});
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [{ errorLogApi, user }] = useAtom(gbState);
  const [loading, setLoading] = useState(true);

  const toggleModal = useCallback(() => {
    setShowModal((prev) => !prev);
  }, []);

  const toggleAddModal = useCallback(() => {
    setAddShowModal((prev) => !prev);
  }, []);

  const getDataFromServer = useCallback(() => {
    setLoading(true);
    let userToken = localStorage.getItem("token");
    let urlLink = `${process.env.REACT_APP_API_URL}/news/${user.church_id}`;

    const config = {
      method: "GET",
      url: urlLink,
      headers: {
        Authorization: `Bearer ${userToken}`,
      },
    };
    axios(config)
      .then(function (response) {
        const oldData = response.data.news;
        const updatedData = oldData.map((data) => ({
          ...data,
          id: data._id,
          title: data.title,
          description: data.description,
        }));
        setData(updatedData);
      })
      .catch(function (err) {
        console.log("error api call ", err);
        errorLogApi(err, "NewsListChild", "/news");
      })
      .finally(() => setLoading(false));
  }, [user.church_id, errorLogApi]);

  const handleDelete = useCallback((row) => {
    confirmAlert({
      title: "",
      message: "Are you sure to delete this News?",
      buttons: [
        {
          label: "Yes",
          onClick: () => deleteRecord(row),
        },
        {
          label: "No",
        },
      ],
    });
  }, []);

  const deleteRecord = useCallback(
    (row) => {
      let userToken = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${userToken}` },
      };
      axios
        .delete(
          `${process.env.REACT_APP_API_URL}/news/delete/${row.id}`,
          config
        )
        .then(() => {
          getDataFromServer();
          toast.success("News deleted successfully");
        })
        .catch((err) => {
          errorLogApi(err, "NewsListChild", "/news");
          toast.error("Failed to delete news");
        });
    },
    [getDataFromServer, errorLogApi]
  );

  const handleImageClick = useCallback((file) => {
    const fileUrl = `${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/news/${file}`;
    window.open(fileUrl, "_blank");
  }, []);

  useEffect(() => {
    if (user.church_id) {
      getDataFromServer();
    }
  }, [user.church_id, getDataFromServer]);

  const filteredData = useMemo(() => {
    return data.filter((news) =>
      Object.values(news).some((value) =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [data, searchTerm]);

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, page, rowsPerPage]);

  const renderTableBody = useCallback(() => {
    return paginatedData.map((row) => (
      <TableRow key={row.id}>
        <TableCell>
          <div className="flex items-center space-x-1">
            <Tooltip title="View details">
              <IconButton
                size="small"
                onClick={() => {
                  toggleModal();
                  setCurrentRecord(row);
                }}
              >
                <FaEye className="text-blue-600" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton
                size="small"
                onClick={() => {
                  toggleAddModal();
                  setCurrentRecord(row);
                }}
              >
                <FaEdit className="text-gray-600" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" onClick={() => handleDelete(row)}>
                <FaTrash className="text-red-600" />
              </IconButton>
            </Tooltip>
          </div>
        </TableCell>
        <TableCell>
          {row.image ? (
            row.image.toLowerCase().endsWith(".pdf") ? (
              <Tooltip title="View PDF">
                <IconButton onClick={() => handleImageClick(row.image)}>
                  <FaFilePdf className="text-red-500" />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="View Image">
                <div className="w-16 h-16 overflow-hidden rounded-md">
                  <img
                    src={`${process.env.REACT_APP_REPORTS_DOWNLOAD_URL}uploads/news/${row.image}`}
                    alt="News"
                    className="object-cover w-full h-full cursor-pointer"
                    onClick={() => handleImageClick(row.image)}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/path/to/placeholder-image.jpg";
                      console.error(`Failed to load image: ${row.image}`);
                    }}
                    crossOrigin="anonymous"
                  />
                </div>
              </Tooltip>
            )
          ) : (
            "No image"
          )}
        </TableCell>
        <TableCell>{row.title}</TableCell>
        <TableCell>{row.description}</TableCell>
        <TableCell>
          {row.createdAt
            ? new Date(row.createdAt).toLocaleDateString("en-US", {
                month: "2-digit",
                day: "2-digit",
                year: "numeric",
              })
            : "N/A"}
        </TableCell>
      </TableRow>
    ));
  }, [
    paginatedData,
    toggleModal,
    toggleAddModal,
    handleDelete,
    handleImageClick,
  ]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="w-full px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">News List</h2>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FaPlus />}
          onClick={() => {
            toggleAddModal();
            setCurrentRecord({});
          }}
        >
          Add News
        </Button>
      </div>

      <div className="mb-4">
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search news..."
          InputProps={{
            startAdornment: <FaSearch className="mr-2 text-gray-400" />,
          }}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Action</TableCell>
              <TableCell>Image</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Created At</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderTableBody()}</TableBody>
        </Table>
      </TableContainer>

      <div className="flex items-center justify-between mt-4">
        <div>
          <span>Rows per page: </span>
          <select
            value={rowsPerPage}
            onChange={(e) => setRowsPerPage(Number(e.target.value))}
          >
            {[5, 10, 25, 50].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                {pageSize}
              </option>
            ))}
          </select>
        </div>
        <div>
          <Button
            disabled={page === 0}
            onClick={() => setPage((prev) => prev - 1)}
          >
            Previous
          </Button>
          <Button
            disabled={paginatedData.length < rowsPerPage}
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <ErrorBoundaryRoutes route="/news" page="ModalView">
        {showModal && (
          <Modal onClose={toggleModal}>
            <h2 className="mb-4 text-xl font-bold">News Detail</h2>
            <NewsView currentRecord={currentRecord} />
          </Modal>
        )}
        {addShowModal && (
          <Modal onClose={toggleAddModal} dialogClassName="modal-90w">
            <h2 className="mb-4 text-xl font-bold">
              {currentRecord?._id ? "Edit" : "Add"} Newsletter
            </h2>
            <hr />
            <NewsAdd
              currentRecord={currentRecord}
              setModel={setAddShowModal}
              getDataFromServer={getDataFromServer}
            />
          </Modal>
        )}
      </ErrorBoundaryRoutes>
    </div>
  );
};

export default React.memo(NewsListChild);
